"use client"
import { useConversationTimeFormatters } from "@/hooks/use-conversation-time-formatters"


import { useQualityCopy } from "@/components/i18n/quality-copy-context"
import { useQualityChanges } from "@/hooks/use-quality-presentations"


import { useId, useState } from "react"

import { Button } from "@/components/ui/button"
import { badgeVariants } from "@/components/ui/badge"
import { DiffLines } from "@/components/ui/prompt-diff"
import { Skeleton } from "@/components/ui/skeleton"
import { STATE_TONE_STYLES } from "@/components/ui/conversation/thread-tones"
import { usePermissions } from "@/hooks/use-permissions"
import { AI_PERMISSIONS, MESSAGING_AGENTS_PERMISSIONS } from "@/lib/auth/permissions.constants"

import { type ChangeState } from "@/lib/conversation-quality/change-display"
import { BASELINE_DAYS } from "@/lib/conversation-quality/suggestions/checkpoints"
import { questionWithKind } from "@/lib/conversation-quality/outcome-display"
import { cn } from "@/lib/utils"
import type { QualityChangesResult } from "@/server-actions/conversation-quality"

import { UndoChangeButton } from "../undo-change-button"
import { EmptyNote, LANDING_SCROLL_MARGIN, SectionCard, useLandedScroll } from "./section-parts"
import { useTranslations } from "next-intl"



type Change = QualityChangesResult["changes"][number]

/** Every change the agents carried and what became of it, newest first: a record, not following the period. */
export function Changes({
  changes,
  total,
  showAgent,
  timezone,
  hasOlder,
  loadingOlder,
  onShowOlder,
  landing,
  onLanded,
}: {
  /** The pages read so far; `undefined` while the first loads. */
  changes: Change[] | undefined;
  total: number;
  /** Under «All agents», each row names its agent. */
  showAgent: boolean;
  /** The viewer's, for the dates. */
  timezone: string;
  hasOlder: boolean;
  loadingOlder: boolean;
  onShowOlder: () => void;
  /** A link opened the tab to land here: scrolled to once the first page is in. */
  landing: boolean;
  onLanded: () => void;
}) {
  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsChangeSections")

  const ref = useLandedScroll<HTMLDivElement>(landing, changes !== undefined, onLanded)

  return (
    <SectionCard
      ref={ref}
      className={LANDING_SCROLL_MARGIN}
      title={tUi("changes")}
      definition={tUi("eachChangeIsCheckedAgainstTheValue0Days", { value0: String(BASELINE_DAYS) })}
      subtitle={tUi("whatTheAgentLearnedAndHowItWorked")}
    >
      <ChangeList
        changes={changes}
        older={hasOlder && changes ? total - changes.length : 0}
        showAgent={showAgent}
        timezone={timezone}
        loadingOlder={loadingOlder}
        onShowOlder={onShowOlder}
      />
    </SectionCard>
  )
}

function ChangeList({
  changes,
  older,
  showAgent,
  timezone,
  loadingOlder,
  onShowOlder,
}: {
  changes: Change[] | undefined;
  /** Changes not listed yet; a change made since the list loaded is not one of them. */
  older: number;
  showAgent: boolean;
  timezone: string;
  loadingOlder: boolean;
  onShowOlder: () => void;
}) {


  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsChangeSections")

  if (!changes) return <Skeleton className="h-24 w-full" />
  if (changes.length === 0) {
    return <EmptyNote text={tUi("changesYouApplyAndChangesTheAgentMakes")} />
  }

  return (
    <div className="space-y-3">
      <ul className="-my-3 divide-y">
        {changes.map((change) => (
          <ChangeRow key={change.id} change={change} showAgent={showAgent} timezone={timezone} />
        ))}
      </ul>
      {older > 0 && (
        <Button variant="link" size="sm" className="h-auto px-0" disabled={loadingOlder} onClick={onShowOlder}>
          <span key={tUi("showValue0OlderChangesPlural", {count: older})}>{tUi("showValue0OlderChangesPlural", {count: older})}</span>
        </Button>
      )}
    </div>
  )
}

function ChangeRow({ change, showAgent, timezone }: { change: Change; showAgent: boolean; timezone: string }) {
  const { formatShortDate } = useConversationTimeFormatters()

  const { TOPIC_CATEGORY_LABELS } = useQualityCopy()
  const { describeChange, describeChangeMade, LEARNED_FROM_TEAM, changeLines, articleLines } = useQualityChanges()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsChangeSections")

  const [open, setOpen] = useState(false)
  const detailsId = useId()
  const canUndo = useCanUndo(change)
  const hasDetails = change.edit !== null || change.article !== null || canUndo
  const { state, line } = describeChange(change, timezone)
  const about = [
    describeChangeMade(change),
    questionWithKind(change.topicLabel, change.topicCategory, TOPIC_CATEGORY_LABELS),
    ...(change.teamOnly ? [LEARNED_FROM_TEAM] : []),
    ...(showAgent ? [change.agentName] : []),
  ].join(" · ")

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="w-14 shrink-0 text-sm text-muted-foreground">{formatShortDate(change.decidedAt, timezone)}</span>
        <div className="min-w-0 flex-1 basis-64">
          <p className="text-sm font-medium leading-snug [text-wrap:pretty]">{change.title}</p>
          <p className="text-xs text-muted-foreground [text-wrap:pretty]">{about}</p>
          <p className="text-xs">{line}</p>
        </div>
        <ChangeStateBadge state={state} />
        {hasDetails && (
          <Button variant="ghost" size="sm" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen(!open)}>
            <span key={open ? tUi("hideDetails") : tUi("details")}>{open ? tUi("hideDetails") : tUi("details")}</span>
          </Button>
        )}
      </div>
      {open && hasDetails && (
        <div id={detailsId} className="mt-2 space-y-2 rounded-lg border bg-muted/30 p-3 sm:ml-[4.5rem]">
          {change.edit && (
            <div className="rounded-md border bg-background p-2.5 text-sm">
              <DiffLines lines={changeLines(change.edit)} />
            </div>
          )}
          {change.article && (
            <div className="rounded-md border bg-background p-2.5 text-sm">
              <DiffLines lines={articleLines(change.article.before, { title: change.title, content: change.article.content })} />
            </div>
          )}
          {canUndo && (
            <div className="flex justify-end pt-1">
              <UndoChangeButton suggestionId={change.id} kind={change.kind} label={tUi("undo")} size="sm" />
            </div>
          )}
        </div>
      )}
    </li>
  )
}

export function ChangeStateBadge({ state }: { state: ChangeState }) {
  const { CHANGE_STATE_TONES, CHANGE_STATE_LABELS } = useQualityChanges()

  return (
    <span
      className={cn(
        badgeVariants({ variant: "outline" }),
        "shrink-0 whitespace-nowrap px-1.5 py-0 text-[11px]",
        STATE_TONE_STYLES[CHANGE_STATE_TONES[state]]
      )}
    >
      {CHANGE_STATE_LABELS[state]}
    </span>
  )
}

/** For someone who manages the agent, and the knowledge base too for an article. */
function useCanUndo(change: Change): boolean {
  const { canUndoChange } = useQualityChanges()

  const { can } = usePermissions()
  const permitted =
    can(MESSAGING_AGENTS_PERMISSIONS.MANAGE) &&
    (change.kind !== "kb_addition" || can(AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE))
  return permitted && canUndoChange(change)
}
